import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Input,
  Row,
  Col,
  Form,
  Select,
  InputNumber,
  AutoComplete,
  Divider,
  Spin,
  Switch,
  Typography,
  Tag,
} from "antd";
import CopagoFields from "./CopagoFields";
import MipresFields from "./MipresFields";
import TAIFields from "./TAIFields";
import debounce from "lodash.debounce";
import api from "../../../../axiosConfig";

const { Option } = Select;
const { Text } = Typography;

const AuthorizationData = ({
  form,
  handleChange,
  cups = [],
  handleCupsChange,
  errors = {},
  isMipresExisting = false,
  handleNoPbsChange,
  propositos = [],
  handlePropositoChange,
  copagoFieldsDisabled = false,
  availableEntregas = [],
  isEditMode, // Nueva prop para identificar modo edición
  actualizarTraslados = false, // Para el estado del switch
  handleActualizarTrasladosChange, // Handler del switch
  trasladosCount = null, // Cantidad de traslados
  loadingTrasladosCount = false, // Loading del conteo
  authorizationData = null, // Datos completos de la autorización
  // Props para TAI
  esTai = false,
  handleTaiCodigoChange,
  handleTaiChange,
  hasPermissionTai = false,
  // Props para transportadora TAI
  transportadoraId,
  onTransportadoraChange,
  // Props para acompañante TAI (solo Intermunicipal)
  conAcompanante = false,
  onConAcompananteChange,
  tieneIntermunicipal = false,
}) => {
  const [loadingNumeroAut, setLoadingNumeroAut] = useState(false);
  const [loadingNumeroPreAut, setLoadingNumeroPreAut] = useState(false);

  // Estado para transportadoras (solo TAI)
  const [transportadoras, setTransportadoras] = useState([]);
  const [loadingTransportadoras, setLoadingTransportadoras] = useState(false);

  // Cargar transportadoras cuando es TAI
  useEffect(() => {
    if (esTai) {
      const fetchTransportadoras = async () => {
        setLoadingTransportadoras(true);
        try {
          const response = await api.get("/transportadoras/activas");
          setTransportadoras(response.data || []);
        } catch (error) {
          console.error("Error al cargar transportadoras:", error);
          setTransportadoras([]);
        } finally {
          setLoadingTransportadoras(false);
        }
      };
      fetchTransportadoras();
    }
  }, [esTai]);
  
  // Estados anteriores para comparación
  const [previousNumeroAut, setPreviousNumeroAut] = useState(authorizationData?.numero_autorizacion || "");
  const [previousNumeroPreAut, setPreviousNumeroPreAut] = useState(authorizationData?.numero_pre_autorizacion || "");
  
  // Actualizar los estados anteriores cuando cambie authorizationData
  useEffect(() => {
    if (authorizationData) {
      setPreviousNumeroAut(authorizationData.numero_autorizacion || "");
      setPreviousNumeroPreAut(authorizationData.numero_pre_autorizacion || "");
    }
  }, [authorizationData]);

  // Función de validación asincrónica con debounce usando useMemo
  const validateUniqueFields = useMemo(
    () =>
      debounce(async (numeroAut, numeroPreAut) => {
        if (!numeroAut && !numeroPreAut) return;

        try {
          if (numeroAut) {
            setLoadingNumeroAut(true);
          }
          if (numeroPreAut) {
            setLoadingNumeroPreAut(true);
          }

          const response = await fetch("/api/validate-authorization", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              numero_autorizacion: numeroAut,
              numero_pre_autorizacion: numeroPreAut,
            }),
          });

          const data = await response.json();

          if (data.numero_autorizacion_exists) {
            handleChange({
              target: {
                name: "errors.numero_autorizacion",
                value: "Este número de autorización ya está registrado.",
              },
            });
          } else {
            handleChange({
              target: { name: "errors.numero_autorizacion", value: "" },
            });
          }

          if (data.numero_pre_autorizacion_exists) {
            handleChange({
              target: {
                name: "errors.numero_pre_autorizacion",
                value: "Este número de pre-autorización ya está registrado.",
              },
            });
          } else {
            handleChange({
              target: { name: "errors.numero_pre_autorizacion", value: "" },
            });
          }
        } catch (error) {
          console.error("Error al verificar unicidad:", error);
          if (numeroAut) {
            handleChange({
              target: {
                name: "errors.numero_autorizacion",
                value: "Error al verificar el número de autorización.",
              },
            });
          }
          if (numeroPreAut) {
            handleChange({
              target: {
                name: "errors.numero_pre_autorizacion",
                value: "Error al verificar el número de pre-autorización.",
              },
            });
          }
        } finally {
          if (numeroAut) {
            setLoadingNumeroAut(false);
          }
          if (numeroPreAut) {
            setLoadingNumeroPreAut(false);
          }
        }
      }, 500),
    [handleChange]
  );

  // Limpiar debounce al desmontar
  useEffect(() => {
    return () => {
      validateUniqueFields.cancel();
    };
  }, [validateUniqueFields]);
  
  // Lógica automática para actualizar categoría de autorización
  useEffect(() => {
    const currentNumeroAut = form.numero_autorizacion || "";
    const currentNumeroPreAut = form.numero_pre_autorizacion || "";
    
    // Estado actual  
    const hasNumeroAut = !!currentNumeroAut;
    const hasNumeroPreAut = !!currentNumeroPreAut;
    
    let nuevaCategoria = form.categoria_autorizacion;
    
    if (isEditMode) {
      // LÓGICA PARA MODO EDICIÓN
      // Detectar cambios
      const numeroAutChanged = currentNumeroAut !== previousNumeroAut;
      const numeroPreAutChanged = currentNumeroPreAut !== previousNumeroPreAut;
      
      // Estado anterior
      const hadNumeroAut = !!previousNumeroAut;
      const hadNumeroPreAut = !!previousNumeroPreAut;
      
      // CASO 1: Solo tenía Pre-autorización y se cambió Pre-autorización
      if (hadNumeroPreAut && !hadNumeroAut && hasNumeroPreAut && !hasNumeroAut && numeroPreAutChanged) {
        nuevaCategoria = "reemplazada";
      }
      // CASO 2: Solo tenía Pre-autorización, no tenía Autorización y se le coloca Autorización
      else if (hadNumeroPreAut && !hadNumeroAut && hasNumeroAut && !numeroPreAutChanged) {
        nuevaCategoria = "autorizacion";
      }
      // CASO 3: Solo tenía Pre-autorización, se edita Pre-autorización Y se coloca Autorización
      else if (hadNumeroPreAut && !hadNumeroAut && hasNumeroAut && hasNumeroPreAut && numeroPreAutChanged) {
        nuevaCategoria = "reemplazada";
      }
      // CASO 4: Tenía ambos y se editan ambos
      else if (hadNumeroPreAut && hadNumeroAut && hasNumeroPreAut && hasNumeroAut && 
               (numeroPreAutChanged || numeroAutChanged)) {
        nuevaCategoria = "reemplazada";
      }
      // CASO 5: Solo tenía Autorización, se coloca Pre-autorización y se elimina Autorización
      else if (hadNumeroAut && !hadNumeroPreAut && !hasNumeroAut && hasNumeroPreAut) {
        nuevaCategoria = "preautorizacion";
      }
      // CASO 6: Solo tenía Autorización y se cambia Autorización
      else if (hadNumeroAut && !hadNumeroPreAut && hasNumeroAut && !hasNumeroPreAut && numeroAutChanged) {
        nuevaCategoria = "autorizacion";
      }
      // CASO 7: Tenía ambos, se elimina Pre-autorización (queda solo Autorización)
      else if (hadNumeroPreAut && hadNumeroAut && !hasNumeroPreAut && hasNumeroAut) {
        nuevaCategoria = "autorizacion";
      }
      // CASO 8: Tenía ambos, se elimina Autorización (queda solo Pre-autorización)
      else if (hadNumeroPreAut && hadNumeroAut && hasNumeroPreAut && !hasNumeroAut) {
        nuevaCategoria = "preautorizacion";
      }
    } else {
      // LÓGICA PARA MODO CREACIÓN (3 casos simples)
      // CASO 1: Solo tiene Pre-autorización
      if (hasNumeroPreAut && !hasNumeroAut) {
        nuevaCategoria = "preautorizacion";
      }
      // CASO 2: Solo tiene Autorización
      else if (hasNumeroAut && !hasNumeroPreAut) {
        nuevaCategoria = "autorizacion";
      }
      // CASO 3: Tiene ambos
      else if (hasNumeroAut && hasNumeroPreAut) {
        nuevaCategoria = "autorizacion";
      }
    }
    
    // Si la categoría calculada es diferente a la actual, actualizarla
    if (nuevaCategoria !== form.categoria_autorizacion) {
      handleChange({ 
        target: { 
          name: "categoria_autorizacion", 
          value: nuevaCategoria 
        } 
      });
    }
  }, [form.numero_autorizacion, form.numero_pre_autorizacion, isEditMode, previousNumeroAut, previousNumeroPreAut]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    handleChange({ target: { name, value } });

    if (name === "numero_autorizacion" || name === "numero_pre_autorizacion") {
      const numeroAut =
        name === "numero_autorizacion" ? value : form.numero_autorizacion;
      const numeroPreAut =
        name === "numero_pre_autorizacion"
          ? value
          : form.numero_pre_autorizacion;
      validateUniqueFields(numeroAut, numeroPreAut);
    }

    if (name === "codigo_mipres" && errors.codigo_mipres) {
      handleChange({
        target: { name: "errors.codigo_mipres", value: "" },
      });
    }
  };

  return (
    <>
      <Divider />

      {/* Se pasa la prop isEditMode a MipresFields */}
      <MipresFields
        form={form}
        handleChange={handleChange}
        handleNoPbsChange={handleNoPbsChange}
        errors={errors}
        isEditMode={isEditMode}
        handleTaiChange={handleTaiChange}
        hasPermissionTai={hasPermissionTai}
      />

      {/* Selector de CUPS - diferente según si es TAI o no */}
      {esTai ? (
        <>
          <Form.Item
            label="Códigos TAI"
            required
            validateStatus={errors.tai_codigo ? "error" : ""}
            help={errors.tai_codigo || "Puede seleccionar uno o más códigos CUPS TAI"}
          >
            <Select
              mode="multiple"
              allowClear
              disabled={isEditMode}
              value={form.tai_codigo || []}
              onChange={handleTaiCodigoChange}
              placeholder="Seleccione uno o más códigos TAI"
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              style={{ width: "100%" }}
            >
              {cups.map((cup) => (
                <Option
                  key={cup.codigo}
                  value={cup.codigo}
                  label={`${cup.codigo} - ${cup.cobertura || cup.descripcion}`}
                >
                  {cup.codigo} - {cup.cobertura || cup.descripcion}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Mostrar coberturas de los CUPS seleccionados */}
          {form.coberturas && (
            <Form.Item label="Coberturas">
              <Input.TextArea
                value={form.coberturas}
                disabled
                rows={2}
                style={{ backgroundColor: "#f5f5f5" }}
              />
            </Form.Item>
          )}

          {/* Selector de transportadora (opcional) */}
          <Form.Item
            label="Transportadora"
            tooltip="Puede asignarse después en el módulo TAI"
          >
            <Select
              value={transportadoraId}
              onChange={onTransportadoraChange}
              placeholder="Seleccione transportadora (opcional)"
              disabled={isEditMode}
              allowClear
              showSearch
              loading={loadingTransportadoras}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children?.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {transportadoras.map((transportadora) => (
                <Option key={transportadora.id} value={transportadora.id}>
                  {transportadora.nombre}
                  {transportadora.es_mogotax_especial && (
                    <Tag color="gold" style={{ marginLeft: 8 }}>
                      Mogotax
                    </Tag>
                  )}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Checkbox de acompañante - solo visible para TAI Intermunicipal */}
          <TAIFields
            tipoTraslado={form.tipo_traslado}
            conAcompanante={conAcompanante}
            onConAcompananteChange={onConAcompananteChange}
            disabled={isEditMode}
            tieneIntermunicipal={tieneIntermunicipal}
          />
        </>
      ) : (
        <Form.Item
          label="CUPS Código"
          required
          validateStatus={errors.cups_codigo ? "error" : ""}
          help={errors.cups_codigo}
        >
          <AutoComplete
            allowClear
            disabled={isEditMode} // Deshabilita en modo edición
            options={cups.map((cup) => ({
              value: cup.codigo,
              label: `${cup.codigo} - ${cup.descripcion}`,
            }))}
            value={form.cups_codigo || ""}
            onSelect={handleCupsChange}
            onChange={(value) =>
              handleChange({ target: { name: "cups_codigo", value } })
            }
            placeholder="Seleccione un código CUPS"
            filterOption={(inputValue, option) =>
              option.label.toUpperCase().includes(inputValue.toUpperCase())
            }
          />
        </Form.Item>
      )}

      {copagoFieldsDisabled && (
        <CopagoFields form={form} handleChange={handleChange} />
      )}

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Número de Entrega">
            {isMipresExisting ? (
              <Select
                name="numero_entrega"
                value={form.numero_entrega || ""}
                onChange={(value) =>
                  handleChange({ target: { name: "numero_entrega", value } })
                }
                style={{ width: "100%" }}
              >
                {availableEntregas.map((num) => (
                  <Option key={num} value={num}>
                    {num}
                  </Option>
                ))}
              </Select>
            ) : (
              <InputNumber
                min={1}
                max={12}
                name="numero_entrega"
                value={form.numero_entrega || ""}
                onChange={(value) =>
                  handleChange({ target: { name: "numero_entrega", value } })
                }
                disabled={form.no_pbs}
                style={{ width: "100%" }}
              />
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Entregas Autorizadas">
            <InputNumber
              min={1}
              max={12}
              name="entregas_autorizadas"
              value={form.entregas_autorizadas || ""}
              onChange={(value) =>
                handleChange({
                  target: { name: "entregas_autorizadas", value },
                })
              }
              disabled={isMipresExisting || form.no_pbs}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="CUPS Descripción">
        <Input
          name="cups_descripcion"
          value={form.cups_descripcion || ""}
          disabled
        />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Tipo de Viaje">
            <Input name="tipo_viaje" value={form.tipo_viaje || ""} disabled />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Tipo de Traslado">
            <Input
              name="tipo_traslado"
              value={form.tipo_traslado || ""}
              disabled
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="N.° Aut"
            required={!form.numero_pre_autorizacion}
            validateStatus={errors.numero_autorizacion ? "error" : ""}
            help={errors.numero_autorizacion}
          >
            <Input
              allowClear
              name="numero_autorizacion"
              value={form.numero_autorizacion || ""}
              onChange={handleInputChange}
              placeholder="Ingrese el número de autorización"
              suffix={loadingNumeroAut && <Spin size="small" />}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="N.° Pre-Aut"
            required={!form.numero_autorizacion}
            validateStatus={errors.numero_pre_autorizacion ? "error" : ""}
            help={errors.numero_pre_autorizacion}
          >
            <Input
              allowClear
              name="numero_pre_autorizacion"
              value={form.numero_pre_autorizacion || ""}
              onChange={handleInputChange}
              placeholder="Ingrese el número de pre-autorización"
              suffix={loadingNumeroPreAut && <Spin size="small" />}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Botón de actualización masiva - Solo en modo edición */}
      {isEditMode && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={24}>
            <Form.Item
              label="Actualizar traslados asociados"
              help={
                actualizarTraslados && trasladosCount !== null ? (
                  loadingTrasladosCount ? (
                    <Spin size="small" />
                  ) : trasladosCount > 0 ? (
                    <Text type="success">
                      Se actualizarán {trasladosCount} traslado{trasladosCount !== 1 ? 's' : ''}
                    </Text>
                  ) : (
                    <Text type="warning">
                      No hay traslados asociados a esta autorización. Solo se actualizarán los números de autorización.
                    </Text>
                  )
                ) : null
              }
            >
              <Switch
                checked={actualizarTraslados}
                onChange={handleActualizarTrasladosChange}
                checkedChildren="Actualizar"
                unCheckedChildren="No actualizar"
                style={{
                  backgroundColor: actualizarTraslados ? "#52c41a" : undefined,
                }}
              />
              {actualizarTraslados && (
                <Text style={{ marginLeft: 12, fontSize: 12, color: '#666' }}>
                  Al activar esta opción, se guardarán los números anteriores y se actualizarán todos los traslados asociados con los nuevos números.
                </Text>
              )}
            </Form.Item>
            
            {/* Mostrar números anteriores si existen */}
            {(authorizationData?.numero_autorizacion_anterior || authorizationData?.numero_pre_autorizacion_anterior) && (
              <div style={{ 
                backgroundColor: '#f0f5ff', 
                padding: '8px 12px', 
                borderRadius: 4,
                border: '1px solid #d6e4ff',
                marginTop: -8
              }}>
                <Text style={{ fontSize: 11, color: '#1890ff', fontWeight: 500, display: 'block', marginBottom: 4 }}>
                  Números anteriores registrados:
                </Text>
                {authorizationData.numero_pre_autorizacion_anterior && (
                  <div style={{ fontSize: 11, color: '#595959', marginBottom: 2 }}>
                    <strong>Pre-Autorización:</strong> {authorizationData.numero_pre_autorizacion_anterior}
                  </div>
                )}
                {authorizationData.numero_autorizacion_anterior && (
                  <div style={{ fontSize: 11, color: '#595959' }}>
                    <strong>Autorización:</strong> {authorizationData.numero_autorizacion_anterior}
                  </div>
                )}
              </div>
            )}
          </Col>
        </Row>
      )}

      <Form.Item
        label="Categoría de Autorización"
        required
        validateStatus={errors.categoria_autorizacion ? "error" : ""}
        help={errors.categoria_autorizacion || "Se actualiza automáticamente según los valores de N.° Aut y N.° Pre-Aut"}
      >
        <Select
          allowClear
          name="categoria_autorizacion"
          value={form.categoria_autorizacion || ""}
          onChange={(value) =>
            handleChange({ target: { name: "categoria_autorizacion", value } })
          }
          placeholder="Seleccione una categoría"
          disabled={true}
        >
          {form.numero_autorizacion && !form.numero_pre_autorizacion ? (
            <>
              <Option value="autorizacion">Autorización</Option>
              <Option value="reemplazada">Reemplazada</Option>
            </>
          ) : form.numero_pre_autorizacion && !form.numero_autorizacion ? (
            <>
              <Option value="preautorizacion">Pre-autorización</Option>
              <Option value="reemplazada">Reemplazada</Option>
            </>
          ) : (
            <>
              <Option value="autorizacion">Autorización</Option>
              <Option value="reemplazada">Reemplazada</Option>
            </>
          )}
        </Select>
      </Form.Item>

      <Form.Item
        label="Tipo de Autorización"
        required
        validateStatus={errors.tipo_autorizacion ? "error" : ""}
        help={errors.tipo_autorizacion}
      >
        <Select
          name="tipo_autorizacion"
          value={form.tipo_autorizacion || ""}
          onChange={(value) =>
            handleChange({ target: { name: "tipo_autorizacion", value } })
          }
          placeholder="Seleccione un tipo"
        >
          <Option value="abierta">Abierta</Option>
          <Option value="cerrada">Cerrada</Option>
        </Select>
      </Form.Item>

      <Form.Item
        label="Propósito"
        required
        validateStatus={errors.proposito ? "error" : ""}
        help={errors.proposito}
      >
        <AutoComplete
          options={propositos.map((proposito) => ({
            value: proposito.descripcion,
            label: proposito.descripcion,
          }))}
          value={form.proposito || ""}
          onSelect={(value) => handlePropositoChange(value)}
          onChange={(value) =>
            handleChange({ target: { name: "proposito", value } })
          }
          placeholder="Seleccione un propósito"
          filterOption={(inputValue, option) =>
            option.label.toUpperCase().includes(inputValue.toUpperCase())
          }
        />
      </Form.Item>

      <Form.Item
        label="Estado de Autorización"
        required
        validateStatus={errors.estado_autorizacion ? "error" : ""}
        help={errors.estado_autorizacion}
      >
        <Select
          name="estado_autorizacion"
          value={form.estado_autorizacion || ""}
          onChange={(value) =>
            handleChange({ target: { name: "estado_autorizacion", value } })
          }
          placeholder="Seleccione un estado"
        >
          <Option value="Activa">Activa</Option>
          <Option value="Suspendida">Suspendida</Option>
          <Option value="Completada">Completada</Option>
          {esTai && <Option value="Anulada">Anulada</Option>}
        </Select>
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Traslados Autorizados"
            required
            validateStatus={errors.total_traslados_asignados ? "error" : ""}
            help={errors.total_traslados_asignados}
          >
            <InputNumber
              min={0}
              max={999}
              precision={0}
              name="total_traslados_asignados"
              value={form.total_traslados_asignados ?? 0}
              onChange={(value) => {
                let processedValue = value;

                if (
                  value !== null &&
                  value.toString().replace(/\D/g, "").length > 3
                ) {
                  processedValue = parseInt(
                    value.toString().replace(/\D/g, "").slice(0, 3)
                  );
                }

                processedValue = Math.max(
                  0,
                  Math.min(999, processedValue || 0)
                );
                processedValue = Number.isInteger(processedValue)
                  ? processedValue
                  : Math.trunc(processedValue);

                handleChange({
                  target: {
                    name: "total_traslados_asignados",
                    value: processedValue,
                  },
                });
              }}
              onKeyDown={(e) => {
                if (e.key === "." || e.key === ",") {
                  e.preventDefault();
                }
              }}
              onBlur={(e) => {
                if (
                  form.total_traslados_asignados === null ||
                  isNaN(form.total_traslados_asignados)
                ) {
                  handleChange({
                    target: {
                      name: "total_traslados_asignados",
                      value: 0,
                    },
                  });
                }
              }}
              style={{ width: "100%" }}
              parser={(value) => {
                return value.replace(/[^\d]/g, "");
              }}
              formatter={(value) => {
                return value !== null && !isNaN(value)
                  ? Math.trunc(value).toString()
                  : "0";
              }}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Kilómetros Autorizados"
            validateStatus={errors.kilometros_autorizados ? "error" : ""}
            help={errors.kilometros_autorizados}
          >
            <InputNumber
              min={0}
              name="kilometros_autorizados"
              value={form.kilometros_autorizados !== undefined && form.kilometros_autorizados !== null ? form.kilometros_autorizados : ""}
              onChange={(value) =>
                handleChange({
                  target: { name: "kilometros_autorizados", value },
                })
              }
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label="Kilómetros por Servicios"
        validateStatus={errors.kilometros_por_servicios ? "error" : ""}
        help={errors.kilometros_por_servicios}
      >
        <InputNumber
          min={0}
          name="kilometros_por_servicios"
          value={form.kilometros_por_servicios !== undefined && form.kilometros_por_servicios !== null ? form.kilometros_por_servicios : ""}
          disabled
          onChange={(value) =>
            handleChange({
              target: { name: "kilometros_por_servicios", value },
            })
          }
          style={{ width: "100%" }}
        />
      </Form.Item>

      <Form.Item
        label="Copago Total"
        validateStatus={errors.copago ? "error" : ""}
        help={errors.copago}
      >
        <InputNumber
          min={0}
          name="copago"
          value={form.copago || 0}
          onChange={(value) =>
            handleChange({ target: { name: "copago", value } })
          }
          style={{ width: "100%" }}
          step={1000}
          precision={2}
          placeholder="Ingrese el copago total"
          onFocus={(e) => {
            e.target.select();
          }}
          formatter={(value) =>
            value ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".") : ""
          }
          parser={(value) =>
            parseFloat(
              value.replace(/\$\s?/g, "").replace(/\./g, "").replace(/,/g, ".")
            ) || 0
          }
          disabled={copagoFieldsDisabled}
        />
      </Form.Item>

      <Form.Item
        label="Copago por Servicios"
        validateStatus={errors.copago_diario ? "error" : ""}
        help={errors.copago_diario}
      >
        <InputNumber
          min={0}
          name="copago_diario"
          value={form.copago_diario || 0}
          onChange={(value) =>
            handleChange({ target: { name: "copago_diario", value } })
          }
          style={{ width: "100%" }}
          step={1000}
          precision={2}
          placeholder="Ingrese el copago por servicios"
          onFocus={(e) => {
            e.target.select();
          }}
          formatter={(value) =>
            value ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".") : ""
          }
          parser={(value) =>
            parseFloat(
              value.replace(/\$\s?/g, "").replace(/\./g, "").replace(/,/g, ".")
            ) || 0
          }
          disabled={copagoFieldsDisabled}
        />
      </Form.Item>
    </>
  );
};

AuthorizationData.propTypes = {
  form: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  cups: PropTypes.array.isRequired,
  handleCupsChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  isMipresExisting: PropTypes.bool.isRequired,
  handleNoPbsChange: PropTypes.func.isRequired,
  propositos: PropTypes.array.isRequired,
  handlePropositoChange: PropTypes.func.isRequired,
  copagoFieldsDisabled: PropTypes.bool,
  availableEntregas: PropTypes.array,
  isEditMode: PropTypes.bool.isRequired, // Nueva prop requerida
  actualizarTraslados: PropTypes.bool,
  handleActualizarTrasladosChange: PropTypes.func,
  trasladosCount: PropTypes.number,
  loadingTrasladosCount: PropTypes.bool,
  authorizationData: PropTypes.object,
  // Props para TAI
  esTai: PropTypes.bool,
  handleTaiCodigoChange: PropTypes.func,
  handleTaiChange: PropTypes.func,
  hasPermissionTai: PropTypes.bool,
  // Props para transportadora TAI
  transportadoraId: PropTypes.number,
  onTransportadoraChange: PropTypes.func,
  // Props para acompañante TAI (solo Intermunicipal)
  conAcompanante: PropTypes.bool,
  onConAcompananteChange: PropTypes.func,
  tieneIntermunicipal: PropTypes.bool,
};

export default AuthorizationData;
